import 'regenerator-runtime/runtime';
import { call, cancel, select, take, takeLatest } from 'redux-saga/effects';
import {
  AngebotsId,
  getInfosFromDeltafaktoren,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  isAngebotsIdEqual,
  QuoteDbInitPayload,
  QuoteDbRequestPayload,
  QuoteDbSpartePayload,
} from '@ps20-frontend/shop-integration-redux';
import { App, getTarifbereich, Produktlinie } from '@ps20-frontend/frontend-models';
import {
  buildTaasRequestModelForShop,
  buildTaasRequestModelForStrecke,
  createHashedTaasData,
  getSelectedProduktlinie,
  getTaasVertragsoptionen,
  TaasData,
} from '@ps20-frontend/preis-lib';
import { Dialogschritt, TaasRequestModel, TaasVwgV2Data, VorschadenArt } from '@ps20/bff-models';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { VwgActions } from '../vwgActions';
import { VorvertragVorschaedenVwg } from '../reducers/transient-state.reducer';
import { IAppState } from '../appState';
import {
  buildTaasVWGDataForShop,
  buildTaasVWGDataForStrecke,
} from './transformer/vwgData.transformer';

export function* watchQuoteDB() {
  yield takeLatest(GlobalActions.QUOTEDB_DATA_REQUESTED, safeSaga(getQuoteDbData, App.VWG_V2));
  yield takeLatest(VwgActions.QUOTEDB_REQUESTED_VWG, safeSaga(quoteDbInit, App.VWG_V2));
}

export function* getQuoteDbData(action: GlobalAction<QuoteDbRequestPayload>) {
  const angebotsIds: AngebotsId[] = action.payload.angebotsIds;
  const globalPut = GlobalPut.getInstance();
  const state: IAppState = yield select();
  let taasRequestModel: TaasRequestModel<TaasVwgV2Data>;
  let selectedProduktlinie: Produktlinie;
  let alleProduktlinien: Array<Produktlinie>;

  const spartenAngebotsId: AngebotsId = {
    angebotsId: state.angebotsId,
    appId: App.VWG_V2,
  };
  if (angebotsIds && !angebotsIds.some(angebot => isAngebotsIdEqual(spartenAngebotsId, angebot))) {
    yield cancel();
    return;
  }
  if (
    (state.produkt && state.gesamtbeitrag !== 0) ||
    state.transientState.fehlerCodeTaas ||
    (state.vertragsoptionen?.beibehalten && state.gesamtbeitragMitRabatt !== 0)
  ) {
    selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    alleProduktlinien = state.produkt.produktlinien;
    if (action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN) {
      taasRequestModel = yield call(buildTaasRequestModel, state);
    } else {
      taasRequestModel = yield call(buildTaasRequestModelShop, state);
    }
    taasRequestModel = addVorbelegteVorschaedenRecordsToTaasRequestModel(
      taasRequestModel,
      state.transientState.vorvertragVorschaeden,
    );
  }

  const quoteDbPayload: QuoteDbSpartePayload<TaasData> = {
    angebotsId: spartenAngebotsId,
    taasSparteRequestModel: taasRequestModel,
    selectedProduktlinie,
    alleProduktlinien,
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    gesamtpreis:
      action.payload.dialogschritt === Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN
        ? state.gesamtbeitrag
        : state.gesamtbeitragMitRabatt,
    vertragslaufzeit: state.vertragsoptionen.vertragslaufzeit,
    antragsnummer: state.antragsnummer,
  };

  getInfosFromDeltafaktoren(state.taasInfo, state.vertragsoptionen, quoteDbPayload);

  yield call(globalPut.put, new GlobalActions().quoteDBDataSparteGenerated(quoteDbPayload));
}

export function* quoteDbInit() {
  const globalPut = GlobalPut.getInstance();
  yield take(GlobalActions.GESAMTPREIS_CHANGED);
  const state: IAppState = yield select();
  const spartenAngebotsId: AngebotsId = {
    angebotsId: state.angebotsId,
    appId: App.VWG_V2,
  };
  const fehlerCodeTaas = state.transientState.fehlerCodeTaas;
  if (fehlerCodeTaas === null) {
    yield cancel();
    return;
  }
  const quoteDbInitPayload: QuoteDbInitPayload = {
    ausloeserAngebotsId: spartenAngebotsId,
    dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
    fehlerCodeTaas,
  };
  yield call([globalPut, globalPut.put], new GlobalActions().quoteDBInit(quoteDbInitPayload));
}

export function buildTaasRequestModel(state: IAppState): TaasRequestModel<TaasVwgV2Data> {
  const taasData: TaasVwgV2Data = buildTaasVWGDataForStrecke(state);
  return buildTaasRequestModelForStrecke(
    getTaasVertragsoptionen(state.vertragsoptionen),
    taasData,
    getTarifbereich(state.versicherungsnehmer),
    state.vnWechselAngaben,
  );
}

export function buildTaasRequestModelShop(state: IAppState): TaasRequestModel<TaasVwgV2Data> {
  const vwgData: TaasVwgV2Data = buildTaasVWGDataForShop(state);
  const newHashedTaasRequest = createHashedTaasData(vwgData);

  return buildTaasRequestModelForShop(
    vwgData,
    newHashedTaasRequest,
    getTaasVertragsoptionen(state.vertragsoptionen),
    state.taasInfo,
    getTarifbereich(state.versicherungsnehmer),
    state.vnWechselAngaben,
  );
}

export function addVorbelegteVorschaedenRecordsToTaasRequestModel(
  taasRequestModel: TaasRequestModel<TaasVwgV2Data>,
  vorvertragVorschaeden: VorvertragVorschaedenVwg,
): TaasRequestModel<TaasVwgV2Data> {
  if (vorvertragVorschaeden != null) {
    return {
      ...taasRequestModel,
      taasSparte: {
        ...taasRequestModel.taasSparte,
        gebaeude: {
          ...taasRequestModel.taasSparte.gebaeude,
          vorschaeden: taasRequestModel.taasSparte.gebaeude.vorschaeden.map(vorschaden => {
            const manuallyEntered = vorschaden.manuallyEntered ?? false;
            return {
              vorschadenArt: vorschaden.vorschadenArt,
              anzahl: manuallyEntered
                ? vorschaden.anzahl
                : vorvertragVorschaeden[getVorschadenKey(vorschaden.vorschadenArt)] || 0,
              manuallyEntered,
            };
          }),
        },
      },
    };
  }

  return taasRequestModel;
}

function getVorschadenKey(stateKey: VorschadenArt): string {
  const vorschadenMap = {
    [VorschadenArt.ELEMENTAR]: 'elementar',
    [VorschadenArt.FEUER]: 'feuer',
    [VorschadenArt.LEITUNGSWASSER]: 'leitungswasser',
    [VorschadenArt.STURM_HAGEL]: 'sturmhagel',
  };
  return (vorschadenMap as Record<string, string>)[stateKey];
}
